import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import {
  draftWithAgentHelperTextByMode,
  getSkillFormNameHelperText,
  isSkillFormNameInvalid,
  SkillFormFields,
} from "../SkillFormModal";
import { SkillDetailHeader } from "./Header/SkillDetailHeader";
import { SkillDetailSkeleton } from "./SkillDetailSkeleton";
import type { SkillDetailPanelProps } from "./types";

export const SkillDetailPanel = ({
  error,
  selection,
  form,
  savedSkillActions,
  onClearError,
}: SkillDetailPanelProps) => {
  const {
    skillId: selectedSkillId,
    skill: selectedSkill,
    isLoading: detailLoading,
  } = selection;
  const {
    mode: formMode,
    values: formValues,
    canCreateGlobal,
    canCreateFormlyInternal,
    onValuesChange: onFormValuesChange,
    onDraftWithAgent: onDraftFormWithAgent,
  } = form;

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {error ? (
        <Alert severity="error" onClose={onClearError}>
          {error}
        </Alert>
      ) : null}

      {formMode && formValues ? (
        <>
          <SkillDetailHeader
            selection={selection}
            form={form}
            savedSkillActions={savedSkillActions}
          />
          <Box
            sx={{ overflow: "auto", py: 0.875, px: 1.3125 }}
            data-testid="skills-form-body"
          >
            <Box sx={{ maxWidth: 920 }}>
              <SkillFormFields
                values={formValues}
                onChange={onFormValuesChange}
                canCreateGlobal={canCreateGlobal}
                canCreateFormlyInternal={canCreateFormlyInternal}
                onDraftWithAgent={onDraftFormWithAgent}
                draftWithAgentLabel="Draft with agent"
                draftWithAgentHelperText={
                  draftWithAgentHelperTextByMode[formMode]
                }
                nameError={isSkillFormNameInvalid(formValues.name)}
                nameHelperText={getSkillFormNameHelperText(formValues.name)}
              />
            </Box>
          </Box>
        </>
      ) : !selectedSkillId ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
          <Typography color="text.secondary">
            Select a skill or create a new one.
          </Typography>
        </Stack>
      ) : detailLoading ? (
        <SkillDetailSkeleton />
      ) : selectedSkill ? (
        <>
          <SkillDetailHeader
            selection={selection}
            form={form}
            savedSkillActions={savedSkillActions}
          />

          <Box sx={{ overflow: "auto" }} data-testid="skills-detail-scroll">
            {!selectedSkill.enabled ? (
              <Alert
                severity="info"
                sx={{
                  alignItems: "center",
                  borderRadius: 0,
                  gap: 1.5,
                  py: 0.875,
                  px: 1.3125,
                  width: "100%",
                  "& .MuiAlert-icon": {
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    m: 0,
                    p: 0,
                  },
                  "& .MuiAlert-message": {
                    m: 0,
                    p: 0,
                  },
                }}
                data-testid="skills-disabled-alert"
              >
                This skill is visible, but disabled for your runs until Enabled
                is turned on.
              </Alert>
            ) : null}

            <Box
              sx={{ py: 0.875, px: 1.3125 }}
              data-testid="skills-detail-body"
            >
              <Stack gap={2.5} sx={{ maxWidth: 920 }}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    textTransform="uppercase"
                  >
                    Description
                  </Typography>
                  <Typography sx={{ mt: 0.5 }}>
                    {selectedSkill.descriptionMd}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    textTransform="uppercase"
                  >
                    Skill content
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 0.75,
                      p: 2,
                      bgcolor: "grey.50",
                      whiteSpace: "pre-wrap",
                      fontFamily:
                        "JetBrains Mono, ui-monospace, SFMono-Regular, monospace",
                      fontSize: 13,
                      lineHeight: 1.65,
                    }}
                  >
                    {selectedSkill.contentMd}
                  </Paper>
                </Box>
              </Stack>
            </Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
};
